import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";

export default function MyAccount() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState(null);
  const [inquiries, setInquiries] = useState(null);

  useEffect(() => {
    if (ready && !user) nav("/auth");
    if (user) {
      api.get("/my/bookings").then(r => setBookings(r.data));
      api.get("/my/inquiries").then(r => setInquiries(r.data));
    }
  }, [user, ready, nav]);

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-gold-dark">My Account</div>
        <h1 className="font-display text-3xl font-bold text-indigo-deep">Namaste, {user.name}</h1>
        <p className="text-sm text-indigo-deep/60">{user.email}</p>
      </div>
      <Tabs defaultValue="bookings">
        <TabsList>
          <TabsTrigger data-testid="tab-my-bookings" value="bookings">My Bookings</TabsTrigger>
          <TabsTrigger data-testid="tab-my-inquiries" value="inquiries">My Inquiries</TabsTrigger>
        </TabsList>
        <TabsContent value="bookings" className="mt-4">
          {bookings === null ? <Skeleton className="h-32 rounded-lg" /> :
            bookings.length === 0 ? (
              <Empty>No bookings yet. <Link to="/packages" className="text-gold-dark font-semibold">Browse packages →</Link></Empty>
            ) : bookings.map(b => (
              <Row key={b.id}>
                <div>
                  <div className="font-semibold text-indigo-deep">{b.entity_name}</div>
                  <div className="text-xs text-indigo-deep/60">{b.entity_type} · {new Date(b.created_at).toLocaleDateString()}</div>
                  {b.check_in && <div className="text-xs text-indigo-deep/70 mt-1">Check-in: {b.check_in} · Guests: {b.guests}</div>}
                </div>
                <div className="text-right">
                  <div className="font-display font-bold text-saffron">₹{Number(b.amount || 0).toLocaleString("en-IN")}</div>
                  <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{b.status}</span>
                </div>
              </Row>
            ))}
        </TabsContent>
        <TabsContent value="inquiries" className="mt-4">
          {inquiries === null ? <Skeleton className="h-32 rounded-lg" /> :
            inquiries.length === 0 ? <Empty>No inquiries yet.</Empty> : inquiries.map(i => (
              <Row key={i.id}>
                <div>
                  <div className="font-semibold text-indigo-deep">{i.entity_name || "General inquiry"}</div>
                  <div className="text-xs text-indigo-deep/60">{new Date(i.created_at).toLocaleDateString()} · {i.travelers} traveler(s)</div>
                  {i.message && <div className="text-xs text-indigo-deep/70 mt-1 line-clamp-2">{i.message}</div>}
                </div>
                <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 h-fit">{i.status}</span>
              </Row>
            ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Row = ({ children }) => <div className="bg-white rounded-lg p-4 mb-2 border border-gold/15 flex items-start justify-between gap-4">{children}</div>;
const Empty = ({ children }) => <div className="text-center py-12 text-indigo-deep/60 bg-white rounded-xl border border-gold/15">{children}</div>;
